import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { IoChevronUp, IoChevronDown, IoSearch } from "react-icons/io5";
import { MdDownload } from "react-icons/md";
import * as XLSX from "xlsx";

/**
 * CustomTable — A feature-rich, zero-MUI table component.
 *
 * Supports: sorting, column resizing, searching, pagination (unlimited records),
 * row click, alternate row colors, header theming, sticky header, and more.
 *
 * All default colors reference CSS custom properties defined in index.css (:root).
 * Pass color props to override per-instance.
 *
 * Column definition shape (compatible with MUI DataGrid):
 *   { field, headerName, width, minWidth, sortable, align, headerAlign,
 *     renderCell, valueGetter }
 *
 * renderCell receives: (params) => JSX
 *   where params = { row, value, field }
 *
 * valueGetter receives: (value, row) => displayValue
 */

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];
const MIN_COL_WIDTH = 60;

/* ------------------------------------------------------------------ */
/*  CustomTable                                                        */
/* ------------------------------------------------------------------ */
const CustomTable = ({
  columns = [],
  rows = [],
  exportName = "users",
  isExport = false,
  getRowId,
  onRowClick,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  searchable = true,
  searchPlaceholder = "Search…",
  headerBgColor,
  headerTextColor,
  rowBgColor,
  alternateRowColor,
  rowHoverColor,
  height = "75vh",
  emptyMessage = "No data available.",
  dense = false,
  /* ---- filter support ---- */
  filterOptions = [], // [{ label: "Active", value: "active" }, ...]
  activeFilter = null, // currently selected filter value
  onFilterChange = null, // (value) => void
  filterLabel = "Filter:", // label shown before filter buttons
  /* ---- selectable support ---- */
  selectable = false, // enable row checkboxes
  selectedKeys = [], // array of selected key values (controlled)
  onSelectionChange = null, // (newSelectedKeys: string[]) => void
  getSelectKey = null, // (row) => string — key used for selection, defaults to getRowId
  /* ---- server-side pagination ---- */
  serverSidePagination = false, // skip internal paging; parent controls page fetching
  serverPage = 1, // current page (1-indexed)
  serverTotalPages = 1, // total number of pages
  onServerPageChange = null, // (pageNumber: number) => void  (1-indexed)
  onServerPageSizeChange = null, // (newPageSize: number) => void
  /* ---- column freezing ---- */
  freezeFirstColumn = false, // sticky first column (+ checkbox if selectable)
  freezeLastColumn = false, // sticky last column
  /* ---- pagination visibility ---- */
  hidePagination = false, // explicitly hide the pagination footer bar
  /* ---- default sort ---- */
  defaultSortField = null, // field to sort by on first render
  defaultSortDir = "asc", // "asc" | "desc"
  isIdBasedPagination,
  limit = 25,
  handlePrev,
  handleNext,
  hasNext,
  hasPrev,
  totalCount,
  totalPages,
  currentPageNo,
  onLimitChange,
}) => {
  /* ---- resolved colors (prop > CSS var > fallback) ---- */
  const colors = {
    headerBg: headerBgColor || "var(--table-header-bg)",
    headerText: headerTextColor || "var(--table-header-text)",
    headerBorder: "var(--table-header-border)",
    rowBg: rowBgColor || "var(--table-row-bg)",
    rowAlt: alternateRowColor || "var(--table-row-alt-bg)",
    rowHover: rowHoverColor || "var(--table-row-hover-bg)",
    border: "var(--table-border)",
    borderLight: "var(--table-border-light)",
    text: "var(--table-text)",
    textMuted: "var(--table-text-muted)",
    textSubtle: "var(--table-text-subtle)",
    placeholder: "var(--table-text-placeholder)",
    toolbarBg: "var(--table-toolbar-bg)",
    paginationBg: "var(--table-pagination-bg)",
    inputBg: "var(--table-input-bg)",
    inputText: "var(--table-input-text)",
    pageBtnBg: "var(--table-page-btn-bg)",
    pageBtnDisabledBg: "var(--table-page-btn-disabled-bg)",
    pageBtnDisabledText: "var(--table-page-btn-disabled-text)",
    pageActiveBg: "var(--table-page-active-bg)",
    pageActiveText: "var(--table-page-active-text)",
    emptyText: "var(--table-empty-text)",
  };

  /* ---- state ---- */
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDir, setSortDir] = useState(defaultSortDir); // "asc" | "desc"
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [colWidths, setColWidths] = useState(() =>
    Object.fromEntries(
      columns?.map((c) => [c.field, c.width || c.minWidth || 150]),
    ),
  );
  const [hoveredRow, setHoveredRow] = useState(null);

  /* refs for resize dragging */
  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startW = useRef(0);

  /* ---- helpers ---- */
  const getRowKey = useCallback(
    (row, idx) => (getRowId ? getRowId(row) : (row.id ?? idx)),
    [getRowId],
  );

  /** Key used for checkbox selection (defaults to row key) */
  const resolveSelectKey = useCallback(
    (row, idx) => {
      if (getSelectKey) return getSelectKey(row);
      return String(getRowId ? getRowId(row) : (row.id ?? idx));
    },
    [getSelectKey, getRowId],
  );

  /** Resolve the display value for a cell */
  const getCellValue = useCallback((col, row) => {
    if (col.valueGetter) {
      return col.valueGetter(row[col.field], row);
    }
    return row[col.field];
  }, []);

  /* ---- search filtering ---- */
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const val = getCellValue(col, row);
        if (val == null) return false;
        return String(val).toLowerCase().includes(q);
      }),
    );
  }, [rows, search, columns, getCellValue]);

  /* ---- selection helpers (controlled) ---- */
  const selectedSet = useMemo(
    () => new Set(selectable ? selectedKeys?.map(String) : []),
    [selectable, selectedKeys],
  );

  const handleToggleRow = useCallback(
    (key) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedSet);
      if (next.has(String(key))) next.delete(String(key));
      else next.add(String(key));
      onSelectionChange([...next]);
    },
    [onSelectionChange, selectedSet],
  );

  const handleToggleAll = useCallback(
    (checked) => {
      if (!onSelectionChange) return;
      if (checked) {
        // Select all currently visible (filtered) rows
        const allKeys = filteredRows?.map((r, i) =>
          String(resolveSelectKey(r, i)),
        );
        const merged = new Set([...selectedSet, ...allKeys]);
        onSelectionChange([...merged]);
      } else {
        // Deselect all currently visible rows
        const visibleKeys = new Set(
          filteredRows?.map((r, i) => String(resolveSelectKey(r, i))),
        );
        const remaining = [...selectedSet].filter((k) => !visibleKeys.has(k));
        onSelectionChange(remaining);
      }
    },
    [onSelectionChange, selectedSet, filteredRows, resolveSelectKey],
  );

  /* ---- sorting ---- */
  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    const col = columns?.find((c) => c.field === sortField);
    if (!col) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      let aVal = getCellValue(col, a);
      let bVal = getCellValue(col, b);

      // Handle nulls
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric comparison
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortField, sortDir, columns, getCellValue]);

  /* ---- pagination ---- */
  const resolvedTotalPages = serverSidePagination
    ? serverTotalPages
    : Math.max(1, Math.ceil(sortedRows?.length / rowsPerPage));
  const paginatedRows = useMemo(
    () =>
      serverSidePagination
        ? sortedRows // server already returned just this page's rows
        : sortedRows?.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedRows, page, rowsPerPage, serverSidePagination],
  );

  // Derive current page for unified rendering
  const currentPage = serverSidePagination ? serverPage - 1 : page; // 0-indexed internally

  // Reset page when data changes (client-side only)
  useEffect(() => {
    if (!serverSidePagination) setPage(0);
  }, [rows, search, sortField, sortDir, rowsPerPage, serverSidePagination]);

  /* ---- unified page handlers ---- */
  const goToPage = (p) => {
    // p is 0-indexed
    if (serverSidePagination) {
      onServerPageChange?.(p + 1); // convert to 1-indexed
    } else {
      setPage(p);
    }
  };

  const handlePageSizeChange = (newSize) => {
    if (serverSidePagination) {
      onServerPageSizeChange?.(newSize);
    } else {
      setRowsPerPage(newSize);
    }
  };

  /* ---- sort handler ---- */
  const handleSort = (col) => {
    if (col.sortable === false) return;
    if (sortField === col.field) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortField(null);
        setSortDir("asc");
      }
    } else {
      setSortField(col.field);
      setSortDir("asc");
    }
  };

  /* ---- column resize handlers ---- */
  const handleResizeStart = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    resizingCol.current = field;
    startX.current = e.clientX;
    startW.current = colWidths[field] || 150;
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(MIN_COL_WIDTH, startW.current + diff);
    setColWidths((prev) => ({ ...prev, [resizingCol.current]: newWidth }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
  }, [handleResizeMove]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const normalizeExcelData = (data, emptyValue = "-") => {
    return data.map((row) => {
      const normalized = {};

      Object.entries(row).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          normalized[key] = emptyValue;
        } else if (Array.isArray(value)) {
          normalized[key] = value.join(", ");
        } else if (typeof value === "boolean") {
          normalized[key] = value ? "Active" : "Inactive";
        } else {
          normalized[key] = value;
        }
      });

      return normalized;
    });
  };

  const exportToExcelOnClick = () => {
    const rawData = rows.map((row) => {
      const item = {};

      columns.forEach((col) => {
        if (col.field === "actions" || col.disableExport) {
          return;
        }

        item[col.headerName || col.field] = col.valueGetter
          ? col.valueGetter(null, row)
          : row[col.field];
      });

      return item;
    });

    const data = normalizeExcelData(rawData);

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, exportName);

    XLSX.writeFile(workbook, `${exportName}.xlsx`);
  };

  /* ---- styles ---- */
  const containerStyle = {
    width: "100%",
    height,
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    overflow: "hidden",
    border: `1px solid ${colors.border}`,
    background: colors.rowBg,
    fontFamily: "'Ubuntu', sans-serif",
    fontSize: "13px",
    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
  };

  const toolbarStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "10px 16px",
    background: colors.toolbarBg,
    borderBottom: `1px solid ${colors.borderLight}`,
    flexWrap: "wrap",
  };

  const searchBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: colors.inputBg,
    border: `1px solid ${colors.border}`,
    borderRadius: "6px",
    padding: "6px 12px",
    minWidth: "220px",
    transition: "border-color 0.2s",
  };

  const searchInputStyle = {
    border: "none",
    outline: "none",
    background: "transparent",
    flex: 1,
    fontSize: "13px",
    color: colors.inputText,
    fontFamily: "'Ubuntu', sans-serif",
  };

  const tableWrapperStyle = {
    flex: 1,
    overflow: "auto",
    position: "relative",
  };

  const tableStyle = {
    width: "max-content",
    minWidth: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  };

  const thStyle = (col, colIndex, totalCols) => ({
    position: "sticky",
    top: 0,
    zIndex:
      freezeFirstColumn && colIndex === 0
        ? 4
        : freezeLastColumn && colIndex === totalCols - 1
          ? 4
          : 2,
    ...(freezeFirstColumn && colIndex === 0
      ? {
          left: selectable ? 50 : 0,
          boxShadow: "4px 0 8px -4px rgba(0, 0, 0, 0.12)",
        }
      : {}),
    ...(freezeLastColumn && colIndex === totalCols - 1
      ? { right: 0, boxShadow: "-4px 0 8px -4px rgba(0, 0, 0, 0.12)" }
      : {}),
    background: colors.headerBg,
    color: colors.headerText,
    padding: dense ? "7px 12px" : "10px 16px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: col.headerAlign || col.align || "left",
    width: colWidths[col.field] || 150,
    minWidth: col.minWidth || MIN_COL_WIDTH,
    maxWidth: colWidths[col.field] || 150,
    whiteSpace: "nowrap",
    userSelect: "none",
    cursor: col.sortable !== false ? "pointer" : "default",
    borderBottom: `1.5px solid ${colors.headerBorder}`,
    borderRight:
      colIndex === totalCols - 1 ? "none" : `1px solid ${colors.borderLight}`,
    transition: "background 0.15s",
  });

  const resizerStyle = {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: "5px",
    cursor: "col-resize",
    background: "transparent",
    zIndex: 3,
    transition: "background 0.15s",
  };

  const tdStyle = (col, colIndex, totalCols) => ({
    padding: dense ? "6px 12px" : "9px 16px",
    textAlign: col.align || "left",
    width: colWidths[col.field] || 150,
    minWidth: col.minWidth || MIN_COL_WIDTH,
    maxWidth: colWidths[col.field] || 150,
    overflow: col.wrap ? "visible" : "hidden",
    textOverflow: col.wrap ? "unset" : "ellipsis",
    whiteSpace: col.wrap ? "normal" : "nowrap",
    wordBreak: col.wrap ? "break-word" : "normal",
    borderBottom: `1px solid ${colors.borderLight}`,
    borderRight:
      colIndex === totalCols - 1 ? "none" : `1px solid ${colors.borderLight}`,
    fontSize: "13px",
    color: colors.text,
    ...(freezeFirstColumn && colIndex === 0
      ? {
          position: "sticky",
          left: selectable ? 50 : 0,
          zIndex: 1,
          background: "inherit",
          boxShadow: "4px 0 8px -4px rgba(0, 0, 0, 0.12)",
        }
      : {}),
    ...(freezeLastColumn && colIndex === totalCols - 1
      ? {
          position: "sticky",
          right: 0,
          zIndex: 1,
          background: "inherit",
          boxShadow: "-4px 0 8px -4px rgba(0, 0, 0, 0.12)",
        }
      : {}),
  });

  const rowStyle = (idx, rowKey) => ({
    background:
      hoveredRow === rowKey
        ? colors.rowHover
        : idx % 2 === 0
          ? colors.rowBg
          : colors.rowAlt,
    cursor: onRowClick ? "pointer" : "default",
    transition: "background 0.15s",
  });

  const paginationStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: colors.paginationBg,
    borderTop: `1px solid ${colors.borderLight}`,
    fontSize: "13px",
    color: colors.textMuted,
    flexWrap: "wrap",
    gap: "8px",
  };

  const pageButtonStyle = (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    background: disabled ? colors.pageBtnDisabledBg : colors.pageBtnBg,
    color: disabled ? colors.pageBtnDisabledText || "red" : colors.inputText,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    fontSize: "13px",
  });

  const selectStyle = {
    padding: "4px 8px",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    fontSize: "13px",
    color: colors.inputText,
    outline: "none",
    cursor: "pointer",
    fontFamily: "'Ubuntu', sans-serif",
  };

  /* ---- render ---- */
  return (
    <>
      {rows?.length && isExport ? (
        <div className="flex justify-end">
          <button
            className="flex items-center gap-1 bg-accent px-3 py-1.5 mb-3 rounded border border-borderColor text-sm font-medium text-white"
            onClick={exportToExcelOnClick}
          >
            <MdDownload size={18} /> Export in Excel
          </button>
        </div>
      ) : (
        ""
      )}

      <div style={containerStyle}>
        {/* Toolbar */}
        {(searchable ||
          filterOptions?.length > 0 ||
          (selectable && selectedSet.size > 0)) && (
          <div style={toolbarStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                flex: 1,
              }}
            >
              {/* Filter buttons */}
              {filterOptions?.length > 0 && onFilterChange && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      color: colors.textMuted,
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    {filterLabel}
                  </span>
                  {filterOptions?.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => onFilterChange(opt.value)}
                      className={
                        activeFilter === opt.value
                          ? "my-button-style-1-selected"
                          : "my-button-style-5"
                      }
                      style={{ fontSize: "13px" }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Search box */}
              {searchable && (
                <div style={searchBoxStyle}>
                  <IoSearch
                    style={{ color: colors.placeholder, flexShrink: 0 }}
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    style={searchInputStyle}
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: colors.placeholder,
                        fontSize: "16px",
                        padding: 0,
                        lineHeight: 1,
                      }}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
              {/* Selection indicator & clear */}
              {selectable && selectedSet.size > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background:
                      "var(--table-page-active-bg, rgba(59, 130, 246, 0.1))",
                    color: "var(--table-page-active-text, #1d4ed8)",
                    padding: "4px 12px",
                    borderRadius: "16px",
                    fontSize: "13px",
                    fontWeight: 600,
                    border:
                      "1px solid var(--table-page-active-bg, rgba(59, 130, 246, 0.2))",
                  }}
                >
                  <span>
                    {selectedSet.size} row{selectedSet.size !== 1 ? "s" : ""}{" "}
                    selected
                  </span>
                  <button
                    onClick={() => handleToggleAll(false)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "inherit",
                      padding: "0 2px",
                      display: "flex",
                      lineHeight: 1,
                      fontSize: "14px",
                    }}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <span style={{ color: colors.textSubtle, fontSize: "13px" }}>
              {filteredRows?.length} record
              {filteredRows?.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Table */}
        <div style={tableWrapperStyle}>
          {paginatedRows?.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: colors.emptyText,
                fontSize: "15px",
                padding: "40px",
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  {/* Select-all checkbox header */}
                  {selectable && (
                    <th
                      style={{
                        ...thStyle({ field: "__select__", sortable: false }),
                        width: 50,
                        minWidth: 50,
                        maxWidth: 60,
                        cursor: "default",
                        textAlign: "center",
                        ...(freezeFirstColumn
                          ? {
                              position: "sticky",
                              left: 0,
                              zIndex: 4,
                              background: colors.headerBg,
                            }
                          : {}),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          filteredRows?.length > 0 &&
                          filteredRows?.every((r, i) =>
                            selectedSet.has(String(resolveSelectKey(r, i))),
                          )
                        }
                        ref={(el) => {
                          if (el) {
                            const allCount = filteredRows?.length;
                            const checkedCount = filteredRows?.filter((r, i) =>
                              selectedSet.has(String(resolveSelectKey(r, i))),
                            )?.length;
                            el.indeterminate =
                              checkedCount > 0 && checkedCount < allCount;
                          }
                        }}
                        onChange={(e) => handleToggleAll(e.target.checked)}
                        style={{
                          width: 16,
                          height: 16,
                          cursor: "pointer",
                          accentColor: "var(--table-page-active-bg)",
                        }}
                        title="Select All"
                      />
                    </th>
                  )}
                  {columns?.map((col, colIndex) => (
                    <th
                      key={col.field}
                      style={thStyle(col, colIndex, columns?.length)}
                      onClick={() => handleSort(col)}
                      title={
                        col.sortable !== false
                          ? `Sort by ${col.headerName}`
                          : undefined
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent:
                            col.headerAlign === "center"
                              ? "center"
                              : col.headerAlign === "right"
                                ? "flex-end"
                                : "flex-start",
                          gap: "4px",
                          position: "relative",
                        }}
                      >
                        <span>{col.headerName}</span>
                        {col.sortable !== false && sortField === col.field && (
                          <span
                            style={{
                              display: "inline-flex",
                              flexDirection: "column",
                              fontSize: "10px",
                              lineHeight: 1,
                            }}
                          >
                            {sortDir === "asc" ? (
                              <IoChevronUp
                                style={{ color: colors.headerBorder }}
                              />
                            ) : (
                              <IoChevronDown
                                style={{ color: colors.headerBorder }}
                              />
                            )}
                          </span>
                        )}
                      </div>
                      {/* Resize handle */}
                      <div
                        style={resizerStyle}
                        onMouseDown={(e) => handleResizeStart(e, col.field)}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background =
                            "var(--table-header-border, #3390FF80)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows?.map((row, idx) => {
                  const rowKey = getRowKey(row, page * rowsPerPage + idx);
                  return (
                    <tr
                      key={rowKey}
                      style={rowStyle(idx, rowKey)}
                      onClick={() => onRowClick && onRowClick({ row })}
                      onMouseEnter={() => setHoveredRow(rowKey)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      {/* Row checkbox */}
                      {selectable && (
                        <td
                          style={{
                            ...tdStyle({ field: "__select__" }),
                            width: 50,
                            minWidth: 50,
                            maxWidth: 50,
                            textAlign: "center",
                            ...(freezeFirstColumn
                              ? {
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  background: "inherit",
                                }
                              : {}),
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSet.has(
                              String(
                                resolveSelectKey(row, page * rowsPerPage + idx),
                              ),
                            )}
                            onChange={() =>
                              handleToggleRow(
                                resolveSelectKey(row, page * rowsPerPage + idx),
                              )
                            }
                            style={{
                              width: 16,
                              height: 16,
                              cursor: "pointer",
                              accentColor: "var(--table-page-active-bg)",
                            }}
                          />
                        </td>
                      )}
                      {columns?.map((col, colIndex) => {
                        const value = getCellValue(col, row);
                        return (
                          <td
                            key={col.field}
                            style={tdStyle(col, colIndex, columns?.length)}
                          >
                            {col.renderCell
                              ? col.renderCell({
                                  row,
                                  value,
                                  field: col.field,
                                })
                              : (value ?? "")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!isIdBasedPagination && !hidePagination && (
          <div style={paginationStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={selectStyle}
              >
                {pageSizeOptions?.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>
                {serverSidePagination
                  ? `Page ${currentPage + 1} of ${resolvedTotalPages}`
                  : sortedRows?.length === 0
                    ? "0 of 0"
                    : `${currentPage * rowsPerPage + 1}\u2013${Math.min(
                        (currentPage + 1) * rowsPerPage,
                        sortedRows?.length,
                      )} of ${sortedRows?.length}`}
              </span>
              <button
                onClick={() => goToPage(0)}
                disabled={currentPage === 0}
                style={pageButtonStyle(currentPage === 0)}
                title="First page"
              >
                «
              </button>
              <button
                onClick={() => goToPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                style={pageButtonStyle(currentPage === 0)}
                title="Previous page"
              >
                &#8249;
              </button>
              <span
                style={{
                  padding: "3px 10px",
                  background: colors.pageActiveBg,
                  color: colors.pageActiveText,
                  borderRadius: "20px",
                  fontWeight: 700,
                  fontSize: "12px",
                  letterSpacing: "0.02em",
                }}
              >
                {currentPage + 1}
              </span>
              <span style={{ color: colors.placeholder, fontSize: "12px" }}>
                / {resolvedTotalPages}
              </span>
              <button
                onClick={() =>
                  goToPage(Math.min(resolvedTotalPages - 1, currentPage + 1))
                }
                disabled={currentPage >= resolvedTotalPages - 1}
                style={pageButtonStyle(currentPage >= resolvedTotalPages - 1)}
                title="Next page"
              >
                &#8250;
              </button>
              <button
                onClick={() => goToPage(resolvedTotalPages - 1)}
                disabled={currentPage >= resolvedTotalPages - 1}
                style={pageButtonStyle(currentPage >= resolvedTotalPages - 1)}
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        )}

        {isIdBasedPagination && (
          <div style={paginationStyle}>
            {/* Rows per page */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Rows per page:</span>
              <select
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                style={selectStyle}
                disabled={rows.length === 0}
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Prev / Next */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={handlePrev}
                disabled={!hasPrev || !!search}
                style={pageButtonStyle(!hasPrev || !!search)}
                title="Previous page"
              >
                &#8249;
              </button>
              <span>
                Page {currentPageNo} of {totalPages} &nbsp;|&nbsp; {totalCount}{" "}
                total records
              </span>
              <button
                onClick={handleNext}
                disabled={!hasNext || !!search}
                style={pageButtonStyle(!hasNext || !!search)}
                title="Next page"
              >
                &#8250;
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomTable;
